import React, { useState, useEffect, useCallback } from 'react';
import { Search, Building2, MapPin, Globe, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import './CompaniesPage.scss';

interface CompanyItem {
  id: number;
  name: string;
  company_code?: string;
  activity_sector?: string;
  city?: string;
  website?: string;
  employee_count?: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CompaniesPage: React.FC = () => {
  const [companies, setCompanies] = useState<CompanyItem[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, pages: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [cities, setCities] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCompanies = useCallback(async (page: number = 1) => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, limit: 20 };
      if (searchTerm) params.q = searchTerm;
      if (selectedCity) params.city = selectedCity;

      const response = await axios.get(`${API_URL}/companies/browse`, { params });
      if (response.data?.success) {
        setCompanies(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCity]);

  useEffect(() => {
    const loadCities = async () => {
      try {
        const response = await axios.get(`${API_URL}/companies/cities`);
        if (response.data?.success) {
          setCities(response.data.data);
        }
      } catch (error) {
        console.error('Error loading cities:', error);
      }
    };
    loadCities();
  }, []);

  useEffect(() => {
    fetchCompanies(1);
  }, [fetchCompanies]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(searchInput);
  };

  const handlePageChange = (page: number) => {
    fetchCompanies(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (pagination.pages <= 1) return null;

    const pages: (number | string)[] = [];
    const current = pagination.page;
    const total = pagination.pages;

    pages.push(1);
    if (current > 3) pages.push('...');

    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
      pages.push(i);
    }

    if (current < total - 2) pages.push('...');
    if (total > 1) pages.push(total);

    return (
      <div className="companies-pagination">
        <button
          className="page-btn"
          onClick={() => handlePageChange(current - 1)}
          disabled={current === 1}
        >
          <ChevronLeft size={18} />
        </button>
        {pages.map((p, i) =>
          typeof p === 'number' ? (
            <button
              key={i}
              className={`page-btn ${p === current ? 'active' : ''}`}
              onClick={() => handlePageChange(p)}
            >
              {p}
            </button>
          ) : (
            <span key={i} className="page-dots">...</span>
          )
        )}
        <button
          className="page-btn"
          onClick={() => handlePageChange(current + 1)}
          disabled={current === total}
        >
          <ChevronRight size={18} />
        </button>
      </div>
    );
  };

  return (
    <div className="companies-page">
      <div className="companies-header">
        <h1>Įmonių katalogas</h1>
        <p>Raskite įmones Lietuvoje — {pagination.total.toLocaleString('lt-LT')} įmonių registre</p>
      </div>

      <div className="companies-filters">
        <form className="search-form" onSubmit={handleSearch}>
          <div className="search-input-wrapper">
            <Search size={18} />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Ieškokite pagal pavadinimą arba įmonės kodą..."
            />
          </div>
          <button type="submit" className="search-btn">Ieškoti</button>
        </form>

        <div className="filter-row">
          <select
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
            className="city-filter"
          >
            <option value="">Visi miestai</option>
            {cities.map(city => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>

          {(searchTerm || selectedCity) && (
            <button
              className="clear-filters"
              onClick={() => {
                setSearchTerm('');
                setSearchInput('');
                setSelectedCity('');
              }}
            >
              Išvalyti filtrus
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="companies-loading">
          <div className="spinner" />
          <p>Kraunamos įmonės...</p>
        </div>
      ) : companies.length === 0 ? (
        <div className="companies-empty">
          <Building2 size={48} />
          <p>Įmonių pagal paieškos kriterijus nerasta</p>
        </div>
      ) : (
        <>
          <div className="companies-grid">
            {companies.map(company => (
              <div key={company.id} className="company-card">
                <div className="company-card-header">
                  <div className="company-icon">
                    <Building2 size={20} />
                  </div>
                  <div>
                    <h3 className="company-name">{company.name}</h3>
                    {company.company_code && (
                      <span className="company-code">Kodas: {company.company_code}</span>
                    )}
                  </div>
                </div>

                <div className="company-card-body">
                  {company.activity_sector && (
                    <div className="company-detail">
                      <span className="detail-label">{company.activity_sector}</span>
                    </div>
                  )}
                  {company.city && (
                    <div className="company-detail">
                      <MapPin size={14} />
                      <span>{company.city}</span>
                    </div>
                  )}
                  {company.website && (
                    <div className="company-detail">
                      <Globe size={14} />
                      <a
                        href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {company.website.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                  {company.employee_count && (
                    <div className="company-detail">
                      <Users size={14} />
                      <span>{company.employee_count} darbuotojų</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {renderPagination()}
        </>
      )}
    </div>
  );
};

export default CompaniesPage;
